/**
 * /devhub/finops — FinOps usage + Matrioska rollup (operator). Surface Layer (/01).
 *
 * REQ-001.91 DevHub UI rebuild · Phase 2 (already-live domains). Live token/cost usage
 * (`/api/control/finops` via `useControlFinops`) as a clickable OperatorDataGrid + the consolidated
 * subtree rollup (`?view=rollup` via `useControlFinopsRollup`) as the Phase-0 TreemapPanel. Local-first:
 * owner inference is unpaid, so cost columns are typically zero by design. Operator-only.
 */

import { InlineNotification } from '@carbon/react';
import type { ChartTabularData } from '@carbon/charts';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import type { OperatorColumn, OperatorDetailMode } from '@contracts/devhub-kit.contract';
import { useEntitlements } from '@lib/persistence/useEntitlements';
import {
  useControlFinops,
  useControlFinopsRollup,
  type ControlFinopsRow,
} from '@lib/persistence/useRuntimeData';
import {
  AiAffordanceLabel,
  OperatorDataGrid,
  OperatorDetailTearsheet,
  TreemapPanel,
} from '../components/devhub-kit';

export const Route = createFileRoute('/devhub/finops')({ component: DevHubFinopsPage });

const COLUMNS: OperatorColumn<ControlFinopsRow>[] = [
  { key: 'operation', header: 'Operation' },
  { key: 'actor', header: 'Actor' },
  { key: 'model_id', header: 'Model' },
  { key: 'total_tokens', header: 'Tokens' },
  { key: 'created_at', header: 'Created' },
];

function DevHubFinopsPage() {
  const { isElevated } = useEntitlements();
  const { usage, isLoading } = useControlFinops(100);
  const { rollup } = useControlFinopsRollup();
  const [selected, setSelected] = useState<ControlFinopsRow | null>(null);
  const [mode, setMode] = useState<OperatorDetailMode>('view');

  if (!isElevated) {
    return (
      <main className="view">
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Operator access required"
          subtitle="FinOps is operator-only."
        />
      </main>
    );
  }

  const treemapChildren = rollup
    .filter((r) => r.used_tokens > 0)
    .map((r) => ({ name: `${r.slug} · ${r.actor}`, value: r.used_tokens, showLabel: true }));
  const treemapData: ChartTabularData = [{ name: 'Used tokens', children: treemapChildren }];

  return (
    <main className="view">
      <header className="view__header">
        <h1 className="view__title">
          FinOps
          <AiAffordanceLabel />
        </h1>
        <p className="view__subtitle">
          Live token usage and the consolidated Matrioska subtree rollup. Owner inference is local and
          unpaid — cost columns are zero by design.
        </p>
      </header>

      <section className="view__section">
        {treemapChildren.length > 0 ? (
          <TreemapPanel title="Used tokens by workspace × actor" data={treemapData} height="360px" />
        ) : (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title="No token usage recorded yet"
            subtitle="The rollup treemap appears once subtree usage is recorded."
          />
        )}
      </section>

      <section className="view__section">
        <OperatorDataGrid
          ariaLabel="FinOps usage"
          columns={COLUMNS}
          rows={usage}
          getRowId={(row) => row.id}
          onRowClick={(row) => {
            setSelected(row);
            setMode('view');
          }}
          loading={isLoading}
          emptyLabel="No usage rows recorded yet."
        />
      </section>

      <OperatorDetailTearsheet
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.operation ?? ''}
        subtitle={selected?.model_id ?? ''}
        mode={mode}
        onModeChange={setMode}
        fields={
          selected
            ? [
                { label: 'Operation', value: selected.operation },
                { label: 'Actor', value: selected.actor },
                { label: 'Model', value: selected.model_id ?? '—' },
                { label: 'Route', value: selected.route ?? '—' },
                { label: 'Prompt tokens', value: String(selected.prompt_tokens ?? 0) },
                { label: 'Completion tokens', value: String(selected.completion_tokens ?? 0) },
                { label: 'Total tokens', value: String(selected.total_tokens ?? 0) },
                { label: 'Cost unit', value: String(selected.cost_unit ?? 0) },
                { label: 'Created', value: selected.created_at },
              ]
            : []
        }
      />
    </main>
  );
}
